"use client";

import React from "react";
import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Chip from "@/components/global/ui/Chip";
import Image from "@/helpers/Image";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { CTAButton } from "@/shadcn/ui/button";

const EASE = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const REVEAL_CHIP = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_TITLE = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_ITEM = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_CTA = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
} as const;
const CHIP_TRANSITION = { duration: 0.7, ease: [...EASE] };
const TITLE_TRANSITION = { duration: 0.8, ease: [...EASE], delay: 0.1 };
const ITEM_TRANSITION_BASE = { duration: 0.8, ease: [...EASE] };
const CTA_TRANSITION = { duration: 0.7, ease: [...EASE], delay: 0.45 };

function GettingStartedSectionDesktop({ data }: { data: PageHomeSchema }) {
  const { gettingStartedChip, gettingStartedTitle, gettingStartedItems, gettingStartedCTA } = data;
  return (
    <section className="hidden lg:block lg:px-0 lg:pb-[18rem]">
      {gettingStartedChip && (
        <motion.div
          className="mx-auto w-fit"
          {...REVEAL_CHIP}
          transition={CHIP_TRANSITION}>
          <Chip
            text={gettingStartedChip}
            animatedIcon
            spinnerSize={32}
            spinnerVariant="strip"
            className="mb-[2rem] prose-p:text-black"
          />
        </motion.div>
      )}

      {gettingStartedTitle && (
        <motion.div
          className="richtext richtext-center w-[min(107.25rem,100%)] mx-auto prose-h1:bg-gradient-to-b prose-h1:from-black prose-h1:to-[#353535] prose-h1:bg-clip-text prose-h1:text-transparent lg:prose-h1:text-[8rem] prose-h1:tracking-normal"
          {...REVEAL_TITLE}
          transition={TITLE_TRANSITION}>
          <PortableText
            value={gettingStartedTitle}
            components={richTextComponent}
          />
        </motion.div>
      )}

      <div className="w-[min(180rem,100%)] gap-[5rem] grid grid-cols-3 mx-auto mt-[7rem]">
        {gettingStartedItems &&
          gettingStartedItems.map((item, index) => (
            <motion.div
              key={index}
              {...REVEAL_ITEM}
              transition={{ ...ITEM_TRANSITION_BASE, delay: 0.25 + index * 0.12 }}
              className="h-full">
              <div className="flex flex-col h-full">
                <div className="bg-black w-fit [&_*]:text-white [&_*]:text-[1.75rem] [&_*]:uppercase [&_*]:leading-[140%] [&_*]:font-spacemono [&_*]:m-0 flex px-[1rem] py-[0.25rem] gap-[6rem]">
                  <p>0{index + 1}</p>
                  <h3>{item.title}</h3>
                </div>
                <div className="flex-1 border border-black/30">
                  <div className="overflow-clip p-[1rem]">
                    <div>
                      <Image
                        src={item.image}
                        alt=""
                        quality={100}
                      />
                    </div>
                  </div>

                  <div className="[&_*]:text-black px-[2rem] pb-[2rem] pt-[3rem] prose-heading:!m-0 prose-p:opacity-70 prose-p:m-0 prose-headings:text-[2.5rem] prose-headings:mb-[1.75rem] prose-headings:!p-0">
                    <PortableText
                      value={item.content}
                      components={richTextComponent}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
      </div>

      {gettingStartedCTA && (
        <motion.div
          className="mx-auto w-fit mt-[4rem]"
          {...REVEAL_CTA}
          transition={CTA_TRANSITION}>
          <CTAButton
            data={gettingStartedCTA}
            className=""
          />
        </motion.div>
      )}
    </section>
  );
}

function GettingStartedSectionMobile({ data }: { data: PageHomeSchema }) {
  const { gettingStartedChip, gettingStartedTitle, gettingStartedItems, gettingStartedCTA } = data;
  return (
    <section className="lg:hidden py-[6rem] px-[3rem]">
      {gettingStartedChip && (
        <div className="mx-auto w-fit">
          <Chip
            text={gettingStartedChip}
            spinnerSize={32}
            spinnerVariant="strip"
            className="mb-[2rem] prose-p:text-black"
          />
        </div>
      )}

      {gettingStartedTitle && (
        <div className="richtext richtext-center w-[min(107.25rem,100%)] mx-auto prose-h1:bg-gradient-to-b prose-h1:from-black prose-h1:to-[#353535] prose-h1:bg-clip-text prose-h1:text-transparent prose-h1:text-[5rem] prose-h1:tracking-normal">
          <PortableText
            value={gettingStartedTitle}
            components={richTextComponent}
          />
        </div>
      )}

      <div className="w-[min(180rem,100%)] gap-[5rem] grid grid-cols-1 mx-auto mt-[4rem]">
        {gettingStartedItems &&
          gettingStartedItems.map((item, index) => (
            <div
              key={index}
              className="h-full">
              <div className="flex flex-col h-full">
                <div className="bg-black w-fit [&_*]:text-white [&_*]:text-[1.75rem] [&_*]:uppercase [&_*]:leading-[140%] [&_*]:font-spacemono [&_*]:m-0 flex px-[1rem] py-[0.25rem] gap-[6rem]">
                  <p>0{index + 1}</p>
                  <h3>{item.title}</h3>
                </div>
                <div className="flex-1 border border-black/30">
                  <div className="overflow-clip p-[1rem]">
                    <div>
                      <Image
                        src={item.image}
                        alt=""
                        quality={100}
                      />
                    </div>
                  </div>

                  <div className="[&_*]:text-black px-[2rem] pb-[2rem] pt-[3rem] prose-heading:!m-0 prose-p:opacity-70 prose-p:m-0 prose-headings:text-[2.5rem] prose-headings:mb-[1.75rem] prose-headings:!p-0">
                    <PortableText
                      value={item.content}
                      components={richTextComponent}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>

      {gettingStartedCTA && (
        <div className="mx-auto w-full mt-[4rem]">
          <CTAButton
            data={gettingStartedCTA}
            className=""
          />
        </div>
      )}
    </section>
  );
}

export default function GettingStartedSection({ data }: { data: PageHomeSchema }) {
  return (
    <>
      <GettingStartedSectionDesktop data={data} />
      <GettingStartedSectionMobile data={data} />
    </>
  );
}
